import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  ArrowLeft,
  Bot,
  CheckCircle,
  Database,
  FileText,
  GitBranch,
  MessageSquare,
  Plug,
  ShieldCheck,
  Workflow,
  Zap,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const whatWeBuild = [
  {
    icon: Workflow,
    title: 'Workflow automation',
    body: 'Multi-step processes wired together with n8n so the handoffs happen without anyone remembering to do them — intake, approvals, reminders, reporting.',
  },
  {
    icon: Bot,
    title: 'Document-grounded AI assistants',
    body: 'Retrieval-augmented chatbots that answer from your own documents and data, with citations, instead of guessing. Scoped so each person only sees what they are allowed to see.',
  },
  {
    icon: Plug,
    title: 'System integrations',
    body: 'Connecting the tools you already pay for — CRM, accounting, storage, calendars, email, payment providers — so data stops being retyped between them.',
  },
  {
    icon: FileText,
    title: 'Document and intake pipelines',
    body: 'Uploads parsed, classified and filed automatically. Resumes, invoices, receipts and forms read by AI and pushed into the right record.',
  },
  {
    icon: Database,
    title: 'Internal portals and dashboards',
    body: 'The admin surface your team actually works in: role-based access, audit trails, and the reports leadership keeps asking for.',
  },
  {
    icon: GitBranch,
    title: 'Scheduled and event-driven jobs',
    body: 'Nightly syncs, webhook listeners, retry-safe queues — the plumbing that keeps an automation reliable rather than merely clever.',
  },
];

const goodFits = [
  'A process that runs on copy-paste between two systems',
  'A shared inbox where requests get triaged by hand',
  'A spreadsheet that three people update and nobody trusts',
  'Reports someone rebuilds manually every Monday',
  'A knowledge base nobody searches because searching it is slow',
  'Onboarding or intake that depends on one person remembering the steps',
];

const AiWorkflowAutomation = () => (
  <div className="min-h-screen pt-16 bg-gradient-to-b from-primary/10 to-background dark:from-primary/5">
    <Helmet>
      <title>AI Workflow Automation &amp; n8n Development | Omnicraft Services</title>
      <meta
        name="description"
        content="We build AI workflow automation for US small businesses: n8n workflows, document-grounded AI assistants, system integrations and internal portals. Book a free automation review."
      />
      <meta
        name="keywords"
        content="ai workflow automation, n8n development, n8n automation agency, business process automation, ai chatbot for business documents, rag chatbot development, workflow automation for small business, custom internal tools, ai automation consultant USA"
      />
      <link rel="canonical" href="https://www.omnicraftservices.com/software/ai-workflow-automation" />
    </Helmet>

    {/* Hero */}
    <section className="py-12 md:py-20">
      <div className="container-custom max-w-4xl mx-auto text-center">
        <Link
          to="/services"
          className="inline-flex items-center text-primary mb-6 hover:underline text-sm md:text-base"
        >
          <ArrowLeft size={16} className="mr-2" /> Back to Services
        </Link>
        <div className="flex flex-col items-center justify-center mb-4">
          <Zap size={48} className="text-primary mb-2" />
          <h1 className="text-4xl md:text-5xl font-bold mb-2">
            AI Workflow Automation
          </h1>
        </div>
        <p className="text-lg md:text-xl text-slate-700 dark:text-slate-300 mb-6">
          Most small teams do not need more software. They need the software they already
          have to stop requiring a human in the middle. We build the automations and AI
          assistants that remove that person from the loop — carefully, and where it is
          safe to do so.
        </p>
        <Button asChild size="lg" className="rounded-full px-8">
          <Link to="/contact">Book a free automation review</Link>
        </Button>
      </div>
    </section>

    {/* What we build */}
    <section className="py-12 md:py-16">
      <div className="container-custom max-w-6xl mx-auto">
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-4 text-primary">
          What we build
        </h2>
        <p className="text-center text-slate-600 dark:text-slate-400 max-w-2xl mx-auto mb-10">
          Every engagement starts with the process, not the tool. The stack follows from
          what the work actually needs.
        </p>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {whatWeBuild.map(({ icon: Icon, title, body }) => (
            <Card key={title} className="h-full border-primary/10">
              <CardContent className="pt-6">
                <Icon className="text-primary mb-3" size={28} />
                <h3 className="text-lg font-semibold mb-2">{title}</h3>
                <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed">
                  {body}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>

    {/* Proof: HR FLARE */}
    <section className="py-12 md:py-16 bg-slate-50 dark:bg-slate-900/50 border-y border-primary/10">
      <div className="container-custom max-w-5xl mx-auto">
        <div className="text-center mb-8">
          <span className="inline-block rounded-full bg-primary/10 px-4 py-1 text-sm font-medium text-primary mb-3">
            Built by us, in production
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-primary">
            HR FLARE — our own AI platform
          </h2>
          <p className="text-slate-600 dark:text-slate-400 mt-3 max-w-2xl mx-auto">
            We do not just wire up other people's tools. HR FLARE is our own product, and
            it is where this capability is proven end to end.
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-2 mb-8">
          <Card className="border-primary/10">
            <CardContent className="pt-6">
              <MessageSquare className="text-primary mb-3" size={28} />
              <h3 className="text-lg font-semibold mb-2">
                A retrieval-augmented assistant
              </h3>
              <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed">
                Answers from live company data and uploaded documents, returns structured
                results with sources, and executes real actions — leave requests, reports,
                announcements. Not a wrapper around a chat box.
              </p>
            </CardContent>
          </Card>
          <Card className="border-primary/10">
            <CardContent className="pt-6">
              <ShieldCheck className="text-primary mb-3" size={28} />
              <h3 className="text-lg font-semibold mb-2">
                Permission-aware by construction
              </h3>
              <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed">
                The assistant can only see what the signed-in person is allowed to see.
                Over 50 granular permissions across four roles, enforced in the database
                rather than in the interface.
              </p>
            </CardContent>
          </Card>
        </div>

        <div className="rounded-2xl border border-primary/10 bg-white dark:bg-slate-900 p-6 md:p-8">
          <h3 className="text-lg font-semibold mb-4">
            Also shipped in the same product
          </h3>
          <ul className="grid gap-3 sm:grid-cols-2">
            {[
              'Model Context Protocol server with OAuth 2.1 — connect Claude or ChatGPT directly',
              'AI resume parsing that fills applications from an uploaded CV',
              'Receipt and expense OCR that drafts the claim for you',
              'Capacity planning built from GitHub issues and Google Calendar',
              'Multi-company tenancy under a single login',
              'GDPR-compliant, bilingual English and German',
            ].map((item) => (
              <li key={item} className="flex items-start gap-2">
                <CheckCircle size={18} className="text-primary mt-0.5 shrink-0" />
                <span className="text-sm text-slate-700 dark:text-slate-300">{item}</span>
              </li>
            ))}
          </ul>
          <div className="mt-6">
            <Button asChild variant="outline" className="rounded-full">
              <a href="https://hrflare.com" target="_blank" rel="noopener noreferrer">
                See HR FLARE
              </a>
            </Button>
          </div>
        </div>
      </div>
    </section>

    {/* Good fits */}
    <section className="py-12 md:py-16">
      <div className="container-custom max-w-4xl mx-auto">
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-4 text-primary">
          Signs this would pay for itself
        </h2>
        <p className="text-center text-slate-600 dark:text-slate-400 mb-8">
          If two or more of these describe your week, there is usually an automation worth
          building.
        </p>
        <ul className="grid gap-3 sm:grid-cols-2">
          {goodFits.map((item) => (
            <li
              key={item}
              className="flex items-start gap-2 rounded-lg border border-primary/10 bg-white/60 dark:bg-slate-900/40 p-4"
            >
              <CheckCircle size={18} className="text-primary mt-0.5 shrink-0" />
              <span className="text-sm text-slate-700 dark:text-slate-300">{item}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>

    {/* How it works */}
    <section className="py-12 md:py-16 bg-slate-50 dark:bg-slate-900/50 border-y border-primary/10">
      <div className="container-custom max-w-4xl mx-auto">
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-10 text-primary">
          How we work
        </h2>
        <ol className="space-y-6">
          {[
            {
              step: '01',
              title: 'A free review of one process',
              body: 'Thirty minutes on a call. You walk us through one process that annoys you. We tell you honestly whether automating it is worth the money.',
            },
            {
              step: '02',
              title: 'A written plan with a price',
              body: 'What gets automated, what stays manual, what it costs, and how long it takes. Fixed scope, no percentage-of-savings games.',
            },
            {
              step: '03',
              title: 'Build, with you watching',
              body: 'You see it working in a staging environment before it touches anything real. Nothing goes live on trust.',
            },
            {
              step: '04',
              title: 'Handover and support',
              body: 'You own the accounts and the workflows from day one. We stay on to maintain them if you want us to, and it is fine if you do not.',
            },
          ].map(({ step, title, body }) => (
            <li key={step} className="flex gap-4">
              <span className="text-2xl font-bold text-primary/40 shrink-0">{step}</span>
              <div>
                <h3 className="text-lg font-semibold mb-1">{title}</h3>
                <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed">
                  {body}
                </p>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </section>

    {/* CTA */}
    <section className="py-12 md:py-16 bg-primary text-white">
      <div className="container-custom max-w-3xl mx-auto text-center">
        <h2 className="mb-4 text-3xl font-bold">
          Tell us about one process you hate
        </h2>
        <p className="mb-8 text-white/90">
          We will tell you whether it is worth automating — including when the answer is
          no. That conversation is free and there is nothing to sign.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button asChild size="lg" variant="secondary" className="rounded-full px-8">
            <Link to="/contact">Start the conversation</Link>
          </Button>
          <Button
            asChild
            size="lg"
            variant="outline"
            className="rounded-full px-8 border-white text-white hover:bg-white hover:text-primary"
          >
            <Link to="/portfolio">See our work</Link>
          </Button>
        </div>
      </div>
    </section>
  </div>
);

export default AiWorkflowAutomation;
